import {
  CustomerLiteModel,
  CustomerModel,
  getCustomersKey,
  useGetCustomers,
} from 'api-hooks/customer';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useMultiSelectInputHelper from 'hooks/use-multi-select-input-helper';
import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  noMargin?: boolean;
  fieldType?: 'control' | 'uncontrol';
}

export function customerMultiTransformer(
  item: CustomerModel | CustomerLiteModel,
): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.alias]) + ` (${item.phone})`,
    customerId: item.id,
  };
}

export default function CustomerMultiInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:customer'),
    placeholder = t('common:select_customer'),
  } = props;
  const { control, setValue } = useFormContext<any>();

  const value = useWatch({ control, name: props.name });
  const permission = useGetCrudPermission('customers');
  const { navigate } = useNavigation();
  const { overrides, ...selectProps } = useMultiSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value || [], {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetCustomers,
    listKeyFunction: getCustomersKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate() {
      navigate(NavigationRoutes.CustomersCreate);
      props.onClose?.();
    },
    listTransformer: React.useCallback((result) => {
      return (result.data || []).map((v) => customerMultiTransformer(v));
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      multi
      filterOptions={null}
      {...selectProps}
    />
  );
}
